import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Button, CircularProgress, Paper, Collapse, IconButton, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Alert } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useUser } from "../../context/userContext";
import { getSalesById, updateSalesByAdmin, generateSaleForms } from "../../api/salesApi";
import { createLog } from "../../api/logApi";
import { toNullIfEmptyOrZero } from "../../utils/priceUtils";
import handleAgentName from "../../utils/userUtils";
import GenericSalesForm from "../common/genericSalesForm";
import { getPricing } from "../../api/resourceApi";
import { getAllAgents, getAllAgentGroups, getAgentsByGroupId } from "../../api/userApi";
import { convertDateStringToDateTime } from "../../utils/datetimeUtils";
import LogViewer from "../common/logViewer";
import SalesDocumentsPanel from "../sales/salesDocumentsPanel";
import ProjectTimeline from "../sales/projectTimeline";
import SaleFormDetails from "../sales/saleFormDetails";
import { buildFrozenPricing } from "../../utils/frozenPricing";
import { methodBreakdown, termForMethod, factorForMethod } from "../../utils/ccPricing";
import { buildClickupExportMsg } from "../sales/clickupExport";
import { requestPaymentLink } from "../../api/paymentLinkApi";
import { getRefundBySale, createRefundRequest, cancelRefundRequest } from "../../api/refundApi";

const PP = "'Poppins', sans-serif";

// Payment-link request: methods + the banks that support each instalment term
// (per the merchant's credit-card instalment plan list). "Any Bank" always allowed.
const PL_METHODS = ['One-off payment', 'Instalment 24 months', 'Instalment 36 months', 'Instalment 60 months'];
const PL_INSTALMENT_BANKS = {
    // CIMB now offers 24-month only — removed from 36m + 60m (2026-06-16).
    'Instalment 24 months': ['BSN', 'CIMB'],
    'Instalment 36 months': ['Maybank', 'Hong Leong', 'HSBC', 'AmBank', 'UOB', 'RHB', 'OCBC', 'Public Bank', 'Affin Bank', 'Standard Chartered'],
    'Instalment 60 months': ['AmBank', 'Standard Chartered', 'Hong Leong Bank', 'Maybank', 'Public Bank'],
};
const PL_ALL_BANKS = ['Maybank', 'Hong Leong', 'HSBC', 'AmBank', 'UOB', 'RHB', 'CIMB', 'OCBC', 'Public Bank', 'Affin Bank', 'Standard Chartered', 'BSN'];
const banksForMethod = (m) => [...(m === 'One-off payment' ? PL_ALL_BANKS : (PL_INSTALMENT_BANKS[m] || [])), 'Any Bank'];

// Colours for the sale-status chip beside Save Changes.
const STATUS_STYLES = {
    Active:   { fg: '#15803D', bg: '#F0FDF4', bd: '#BBF7D0' },
    Pending:  { fg: '#B45309', bg: '#FEF3C7', bd: '#FDE68A' },
    Refunded: { fg: '#B91C1C', bg: '#FEE2E2', bd: '#FECACA' },
    Archived: { fg: '#475569', bg: '#F1F5F9', bd: '#E2E8F0' },
};

export default function EditSalePage() {
    const { salesId } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();

    const isSuperAdmin = user?.roles?.includes("Super Admin");
    // Co-broke "Adjustment Edit": only a Super Admin carrying the AdjustmentEdit
    // right may add/change/remove the co-broke agent on an existing sale. The
    // backend re-verifies this right AND blocks the change if commission is
    // already paid (void the slip first). Everyone else sees co-broke read-only.
    const canEditCobroke = isSuperAdmin && !!user?.roleRights?.includes("AdjustmentEdit");
    const isAsternAdmin = user?.roles?.includes("Astern Admin");
    const isGroupAdmin = user?.roles?.includes("Group Admin") && !user?.roles?.some(r => ['Super Admin', 'Accountant', 'Admin'].includes(r));
    const isAdminOnly = (user?.roles?.includes("Admin") && !user?.roles?.includes("Super Admin") && !user?.roles?.includes("Accountant")) || isAsternAdmin;
    // Group Admin now edits a PENDING sale fully, like a sales person (user 2026-06-11) —
    // no longer remark-only. (Previously: const isRemarkOnly = isGroupAdmin;) So a Group
    // Admin gets agentEditable on Pending sales (read-only once Active), same as an agent.
    const isRemarkOnly = false;
    // Doc upload is allowed for everyone who reaches the page.
    const canUploadDocs = !isRemarkOnly || isGroupAdmin;
    // Accountant / Super Admin may always edit. (Status-aware edit gates that also
    // let the agent edit a PENDING sale are computed below, after saleStatus.)
    const isPrivileged = !isAsternAdmin && !isRemarkOnly && user?.roles?.some(r => ['Accountant', 'Super Admin'].includes(r));
    // Only Super Admin / Accountant may manually override the sale status.
    const canEditStatus = user?.roles?.some(r => ['Accountant', 'Super Admin'].includes(r));
    // Project Timeline milestone dates — only Group Admin / Admin / Super Admin /
    // Accountant may edit; everyone else (agents, leaders, viewer, …) is read-only.
    const canEditTimeline = user?.roles?.some(r => ['Accountant', 'Super Admin', 'Admin', 'Group Admin'].includes(r));
    // Referral on a sale is locked once an agent saves — only Accountant / Super
    // Admin can change it during amend. Agents (and other roles) view-only here.
    const canEditReferral = user?.roles?.some(r => ['Accountant', 'Super Admin'].includes(r));

    // Clean list (matches Create Sale). Legacy values (CC / CC 36M / CC 60M / LOAN)
    // were removed; a sale still holding one is re-added below so it still displays.
    // Values MUST match Create Sale ("creditCard12/24/36/60", "CASH") so a sale
    // saved from the create form matches an option here — otherwise the saved
    // value gets appended as a stray raw option (e.g. "creditCard60").
    const BASE_PAYMENT_METHODS = [
        { label: "CASH", value: "CASH" },
        { label: "CREDIT CARD 12 MONTHS", value: "creditCard12" },
        { label: "CREDIT CARD 24 MONTHS", value: "creditCard24" },
        { label: "CREDIT CARD 36 MONTHS", value: "creditCard36" },
        { label: "CREDIT CARD 60 MONTHS", value: "creditCard60" },
        { label: "MIX (CASH + CC)", value: "mix" },
    ];
    // Snap any saved payment-method string (legacy "CREDITCARD60", "CC 60M", etc.)
    // onto the canonical create-form value so it matches a dropdown option.
    const normalizePaymentMethod = (pm) => {
        const s = String(pm || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        if (!s) return '';
        if (s === 'cash') return 'CASH';
        if (s.includes('mix')) return 'mix';
        if (s.includes('12')) return 'creditCard12';
        if (s.includes('24')) return 'creditCard24';
        if (s.includes('36')) return 'creditCard36';
        if (s.includes('60')) return 'creditCard60';
        return pm; // unknown (generic CC / LOAN) → keep raw; appended for display
    };

    const [sale, setSale] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [logRefreshKey, setLogRefreshKey] = useState(0);
    const [historyOpen, setHistoryOpen] = useState(false);

    // Request Payment Link dialog
    const [plOpen, setPlOpen] = useState(false);
    const [plBank, setPlBank] = useState("");
    const [plMethod, setPlMethod] = useState("One-off");
    const [plAmount, setPlAmount] = useState("");
    const [plNumLinks, setPlNumLinks] = useState(1);
    const [plSubmitting, setPlSubmitting] = useState(false);
    const [plMsg, setPlMsg] = useState(null); // { type, text }
    const [refundReq, setRefundReq] = useState(null);   // open (Pending) refund request, or null
    const [refundDlgOpen, setRefundDlgOpen] = useState(false);
    const [refundReason, setRefundReason] = useState("");
    const [refundBusy, setRefundBusy] = useState(false);
    const [formDetailsOpen, setFormDetailsOpen] = useState(false);
    const [formPayload, setFormPayload] = useState(null);
    // RSC-style promos (manual sales only) — restored from the snapshot on load.
    const [promoBackupbox, setPromoBackupbox] = useState(false);
    const [promoFirst640, setPromoFirst640] = useState(false);
    const [promoPerBatt, setPromoPerBatt] = useState(false);
    const [clickupCopied, setClickupCopied] = useState(false);
    const [saleStatus, setSaleStatus] = useState('Pending');
    const statusTouched = useRef(false);

    // Edit permissions are STATUS-aware:
    //  - Accountant / Super Admin: may always edit.
    //  - Sales agent (not admin-only / remark-only): may edit ALL inputs only while
    //    the sale is PENDING. Once Active / Refunded / Archived it is read-only for them.
    const isPendingSale = (saleStatus || 'Pending') === 'Pending';
    const agentEditable = !isAdminOnly && !isRemarkOnly && isPendingSale;
    const canEditDetails = isPrivileged || agentEditable;   // customer / product / form details
    const canEditPricing = isPrivileged || agentEditable;   // pricing + add-ons
    const canEditOriPrice = canEditPricing;
    const [lineItems, setLineItems] = useState([]);

    const [originalPrice, setOriginalPrice] = useState(0);
    // Original Cash Price (ATAP reference) — edit-in-place two-box; reference only.
    const [ocp, setOcp] = useState('');
    const [finalPrice, setFinalPrice] = useState(0);
    const [addOnPrice, setAddOnPrice] = useState(0);
    const [asternDiscount, setAsternDiscount] = useState(0);
    const [agentDiscount, setAgentDiscount] = useState(0);
    // The sale's stored (Section-2) values, kept so an UNTOUCHED edit re-saves them
    // unchanged. The cash inputs (Section 1) start blank — only when the agent edits
    // them does the price recompute. No silent re-pricing of existing sales.
    const [storedPricing, setStoredPricing] = useState(null);

    const [customerName, setCustomerName] = useState("");
    const [customerEmail, setCustomerEmail] = useState("");
    const [customerPhone, setCustomerPhone] = useState("");
    const [customerAddress, setCustomerAddress] = useState("");
    const [customerState, setCustomerState] = useState("");
    const [customerCity, setCustomerCity] = useState("");
    const [customerPostcode, setCustomerPostcode] = useState("");

    // Co-broke editing (Adjustment Edit). Options + selection mirror create-sales.
    // Only populated/editable when canEditCobroke; otherwise the form shows the
    // existing co-broke read-only (no-op setters, as before).
    const [coBrokeGroupOptions, setCoBrokeGroupOptions] = useState([]);
    const [coBrokeNameOptions, setCoBrokeNameOptions] = useState([]);
    const [selectedCoBrokeGroupId, setSelectedCoBrokeGroupId] = useState("");
    const [selectedCoBrokeAgentId, setSelectedCoBrokeAgentId] = useState("");

    const [productList, setProductList] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("RESIDENTIAL");
    const [panelQty, setPanelQty] = useState(0);
    const [energy, setEnergy] = useState(0);
    const [residentialMatch, setResidentialMatch] = useState(null);
    const [canEditPrice, setCanEditPrice] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("");
    // RSC-style sizing verdict from the form — gates save when the config is changed.
    const [sizing, setSizing] = useState(null);
    const [phaseMode, setPhaseMode] = useState("single");
    const [depositOn, setDepositOn] = useState("");
    const [remarks, setRemarks] = useState("");

    const [referralOptions, setReferralOptions] = useState([]);
    const [selectedReferralId, setSelectedReferralId] = useState('');

    const userModified = useRef(false);
    // True when the agent edits any Form-Details (snapshot) field — IC/TNB/etc. appear
    // on every PDF, so such a change regenerates ALL forms on save.
    const formDetailsTouched = useRef(false);

    const fetchSale = useCallback(async () => {
        try {
            const res = await getSalesById(salesId);
            const s = res.data;
            setSale(s);

            // Keep the stored (Section-2) values for the read-only saved panel.
            setStoredPricing({
                originalPrice: s.originalPrice, finalPrice: s.finalPrice, addOnPrice: s.addOnPrice,
                asternDiscount: s.discountByAstern, agentDiscount: s.discountByAgent,
            });
            // Parse the stored form snapshot up front so we can repopulate Section 1.
            let parsed = null;
            if (s.formPayloadJson) {
                try { parsed = JSON.parse(s.formPayloadJson); } catch { parsed = null; }
            }
            // Section 1 (cash): NEW sales persist the cash inputs in section1Cash —
            // repopulate them so the cash the agent typed REMAINS visible after save.
            // OLD sales (no section1Cash) stay BLANK so they aren't re-priced unless
            // the agent enters a cash price.
            const s1 = parsed?.section1Cash;
            if (isPrivileged) {
                // Edit-in-place (Accountant / Super Admin): load the STORED values
                // DIRECTLY as the Original Sales Price (the commission basis) so the
                // field is never blank and saves without any cash↔CC conversion — the
                // only thing that's correct given the inconsistent stored basis across
                // sales. OCP (Original Cash Price) = the ATAP cash reference recorded in
                // the snapshot, used only for the mismatch warning.
                const _ori = Number(s.originalPrice) || 0;
                const _fin = Number(s.finalPrice) || 0;
                const _ast = Number(s.discountByAstern) || 0;
                const _agt = Number(s.discountByAgent) || 0;
                // Implied-discount: a promo (Backupbox / FIRST640, etc.) can be baked into
                // FinalPrice but NOT recorded in the discount columns — so Original − Astern
                // − Agent over-states the price (shows 41,006 when the real Final is 36,906).
                // Surface that gap as Astern so the displayed Final matches the stored Final
                // (the commission basis). Commission is unchanged (Final stays the same).
                const _gap = (_ori > 0 && _fin > 0) ? Math.max(0, _ori - _fin - _ast - _agt) : 0;
                setOriginalPrice(_ori > 0 ? _ori : '');
                setAsternDiscount(_ast + _gap);
                setAgentDiscount(_agt);
                // Leave OCP blank so it FOLLOWS the live ATAP dated-list reference in the
                // form (Original Cash Price box derives to the list price, e.g. 36,900) —
                // not the sale's frozen/manual snapshot value. User can still override it.
                setOcp('');
            } else if (s1 && Number(s1.originalPrice) > 0) {
                // Manual create-form sale — exact cash inputs the agent typed.
                setOriginalPrice(s1.originalPrice);
                setAsternDiscount(s1.asternDiscount ?? 0);
                setAgentDiscount(s1.agentDiscount ?? 0);
            } else if (parsed && Number(parsed.oriCashPrice) > 0) {
                // RSC/CSC sale — populate Section 1 (cash) from the calculator snapshot
                // so Original Price / Astern / Agent show what RSC suggested (not blank).
                setOriginalPrice(parsed.oriCashPrice);
                setAsternDiscount(parsed.asternDiscount ?? 0);
                setAgentDiscount(parsed.agentDiscount ?? 0);
            } else {
                setOriginalPrice('');
                setAsternDiscount('');
                setAgentDiscount('');
            }
            setFinalPrice('');
            setAddOnPrice(s.addOnPrice);
            setCustomerName(s.customerDetails?.customerName ?? "");
            setCustomerEmail(s.customerDetails?.email ?? "");
            setCustomerPhone(s.customerDetails?.phoneNumber ?? "");
            setCustomerAddress(s.customerDetails?.address ?? "");
            setCustomerState(s.customerDetails?.state ?? "");
            setCustomerCity(s.customerDetails?.city ?? "");
            setCustomerPostcode(s.customerDetails?.postcode ?? "");
            setSelectedCategory(s.productCategory);
            setPanelQty(s.panelQty || 0);
            setEnergy(s.energy || 0);
            setPaymentMethod(normalizePaymentMethod(s.paymentMethod));
            setPhaseMode(s.phase || "single");
            setDepositOn(s.depositOn ?? "");
            setRemarks("");
            setSelectedReferralId(s.referralUserId ?? '');
            setResidentialMatch(s.productCategory === "RESIDENTIAL" ? true : null);
            setLineItems(s.lineItems || []);
            setFormPayload(parsed);
            // Restore the saved promo ticks so the edit page shows what was applied at
            // the sale. On edit they are LOCKED (read-only, see genericSalesForm) so
            // they're informational only and never re-arm the Astern auto-fill — the
            // saved discount stays the source of truth (price-freeze rule). The agent
            // edits the Astern Discount field directly to change it.
            setPromoBackupbox(!!parsed?.promoBackupbox);
            setPromoFirst640(!!parsed?.promoFirst640);
            setPromoPerBatt(!!parsed?.promoPerBatt);
            setSaleStatus(s.status || 'Pending');
            statusTouched.current = false;
            userModified.current = false;
        } catch (err) {
            console.error("Failed to load sale:", err);
        } finally {
            setLoading(false);
        }
    }, [salesId]);

    useEffect(() => { fetchSale(); }, [fetchSale]);

    // Co-broke editing: load the group list + seed the current co-broke selection
    // once (when the Super Admin has the AdjustmentEdit right and the sale is in).
    useEffect(() => {
        if (!canEditCobroke || !sale) return;
        let alive = true;
        (async () => {
            try {
                const groupsRes = await getAllAgentGroups();
                if (alive) setCoBrokeGroupOptions(groupsRes.data || []);
            } catch { /* leave options empty */ }
            const curGroupId = sale.coAgentDetails?.groupId ?? "";
            const curAgentId = sale.coAgentDetails?.userId ?? "";
            if (alive) {
                setSelectedCoBrokeGroupId(curGroupId);
                setSelectedCoBrokeAgentId(curAgentId);
            }
            if (curGroupId) {
                try {
                    const agentsRes = await getAgentsByGroupId(curGroupId);
                    // Exclude the main agent so co-broke can't equal the seller.
                    const filtered = (agentsRes.data || []).filter(a => a.userId !== sale.agentDetails?.userId);
                    if (alive) setCoBrokeNameOptions(filtered);
                } catch { /* leave names empty */ }
            }
        })();
        return () => { alive = false; };
    }, [canEditCobroke, sale]);

    // Group picker change → load that group's agents + reset the selected agent.
    const handleCoBrokeGroupChange = useCallback(async (e) => {
        const gid = e?.target?.value ?? "";
        setSelectedCoBrokeGroupId(gid);
        setSelectedCoBrokeAgentId("");
        userModified.current = true;
        if (!gid) { setCoBrokeNameOptions([]); return; }
        try {
            const res = await getAgentsByGroupId(gid);
            setCoBrokeNameOptions((res.data || []).filter(a => a.userId !== sale?.agentDetails?.userId));
        } catch { setCoBrokeNameOptions([]); }
    }, [sale]);

    const loadRefund = useCallback(async () => {
        if (!salesId) return;
        try {
            const r = await getRefundBySale(salesId);
            const list = Array.isArray(r.data) ? r.data : [];
            setRefundReq(list.find(x => x.status === 'Pending') || null);
        } catch { setRefundReq(null); }
    }, [salesId]);
    useEffect(() => { loadRefund(); }, [loadRefund]);

    const submitRefundRequest = async () => {
        if (!refundReason.trim()) { alert("Please enter a refund reason."); return; }
        setRefundBusy(true);
        try {
            await createRefundRequest({ salesId: sale.salesId, reason: refundReason.trim() });
            await createLog({
                module: "Sales", recordId: sale.salesId, action: "Refund Requested",
                remarks: `Refund requested. Reason: ${refundReason.trim()}`, createdBy: user.username ?? ""
            });
            setRefundDlgOpen(false);
            setRefundReason("");
            setLogRefreshKey(k => k + 1);
            await loadRefund();
        } catch (err) {
            alert("Could not request refund: " + (err?.response?.data || err?.message || "unknown error"));
        } finally { setRefundBusy(false); }
    };

    const cancelRefund = async () => {
        if (!refundReq) return;
        if (!window.confirm("Cancel this refund request?")) return;
        setRefundBusy(true);
        try {
            await cancelRefundRequest(refundReq.refundRequestId);
            await createLog({
                module: "Sales", recordId: sale.salesId, action: "Refund Cancelled",
                remarks: "Refund request cancelled.", createdBy: user.username ?? ""
            });
            setLogRefreshKey(k => k + 1);
            await loadRefund();
        } catch (err) {
            alert("Could not cancel refund: " + (err?.response?.data || err?.message || "unknown error"));
        } finally { setRefundBusy(false); }
    };

    useEffect(() => {
        getAllAgents().then(r => {
            const refs = (r.data || []).filter(u =>
                u.status === 'Active' && (u.userRoles || []).some(ur => ur.roleName === 'Referral')
            );
            setReferralOptions(refs);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (canEditOriPrice) {
            getPricing().then(r => setProductList((r.data || []).filter(p => p.listName === 'Residential Pricing'))).catch(() => {});
        }
    }, [canEditOriPrice]);

    useEffect(() => {
        const isCommercial = selectedCategory === "COMMERCIAL";
        setCanEditPrice(!isRemarkOnly && (isCommercial || canEditOriPrice));
    }, [selectedCategory, canEditOriPrice]);

    useEffect(() => {
        if (selectedCategory === "RESIDENTIAL" && energy > 0 && canEditOriPrice && userModified.current) {
            const match = productList.find(p => Number(p.energy) === Number(energy));
            setResidentialMatch(match);
            if (match) {
                // Section 1 = CASH base — auto-fill the cash column when the agent
                // changes the panel count (gated on userModified → never on load).
                const cash = phaseMode === '3phase'
                    ? (Number(match.cash3p8k) || Number(match.cashPrice) || 0)
                    : (Number(match.cashPrice) || Number(match.cash3p8k) || 0);
                setOriginalPrice(cash);
            }
        }
    }, [selectedCategory, panelQty, energy, paymentMethod, productList, canEditOriPrice]);

    useEffect(() => {
        const afterDisc = (originalPrice || 0) - (asternDiscount || 0) - (agentDiscount || 0);
        setFinalPrice(Math.max(0, afterDisc));
    }, [originalPrice, asternDiscount, agentDiscount]);

    // Post a standalone remark to the sale's history (a comment), independent of the
    // full Save Changes flow — the remarks field sits far below the Save button.
    const handleSaveRemark = async () => {
        if (!remarks.trim()) { alert("Please enter a remark."); return; }
        try {
            await createLog({
                module: "Sales", recordId: sale.salesId, action: "Remark",
                remarks: remarks.trim(), createdBy: user.username ?? ""
            });
            setRemarks("");
            setLogRefreshKey(k => k + 1);
        } catch (err) {
            console.error("Failed to save remark:", err);
            alert("Failed to save remark.");
        }
    };

    const handleSave = async () => {
        // Remark-only mode: just create a log entry with the remark
        if (isRemarkOnly) {
            if (!remarks.trim()) { alert("Please enter a remark."); return; }
            setSaving(true);
            try {
                await createLog({
                    module: "Sales", recordId: sale.salesId, action: "Remark",
                    remarks: remarks.trim(), createdBy: user.username ?? ""
                });
                setRemarks("");
                setLogRefreshKey(k => k + 1);
            } catch (err) {
                console.error("Failed to submit remark:", err);
                alert("Failed to submit remark.");
            } finally {
                setSaving(false);
            }
            return;
        }

        // No residential match: allow manual price entry (originalPrice validation in caller catches RM 0).

        // RSC-style sizing gate — enforce only when the user actually changed the
        // config (so saving an untouched old sale isn't blocked by a legacy config
        // that predates the CCC rule). Over-SEDA needs a CCC add-on; ratio in cap.
        if (userModified.current && sizing && !sizing.valid) {
            alert(sizing.reason || "This configuration is not valid. Fix the inverter/SEDA/CCC issue before saving.");
            return;
        }

        // Status guard — editing a Refunded/Archived sale's PRICE changes commission on
        // a sale that's already closed out. Confirm before proceeding.
        const _priceChanged = isPrivileged && (
            Number(originalPrice) !== (Number(sale.originalPrice) || 0) ||
            Number(asternDiscount) !== (Number(sale.discountByAstern) || 0) ||
            Number(agentDiscount) !== (Number(sale.discountByAgent) || 0));
        if (_priceChanged && (saleStatus === 'Refunded' || saleStatus === 'Archived')) {
            if (!window.confirm(`This sale is ${saleStatus}. Changing its price will recompute commission on a closed sale. Continue?`)) {
                return;
            }
        }

        setSaving(true);

        // Section 1 (cash) edited? If the agent entered a cash Original Price, convert
        // it to the chosen payment-method basis (Section 2) and save that. If blank
        // (untouched), keep the stored Section-2 values — no silent re-pricing.
        const s1Edited = originalPrice !== '' && originalPrice != null && Number(originalPrice) > 0;
        let pOriginal, pFinal, pAstern, pAgent, pAddon;
        if (isPrivileged && s1Edited) {
            // Edit-in-place (Accountant / Super Admin): the inputs ARE the stored
            // (payment-method) basis = the Original Sales Price. Store them DIRECTLY —
            // NO methodBreakdown conversion. This is the only safe path given the
            // inconsistent cash/CC stored basis: an untouched re-save reproduces the
            // exact stored number, and commission (= Final) is exactly what's shown.
            // Add-on is preserved as-is (never reconverted → no revenue inflation).
            pOriginal = Number(originalPrice) || 0;
            pAstern = Number(asternDiscount) || 0;
            pAgent = Number(agentDiscount) || 0;
            pAddon = Number(addOnPrice) || 0;
            // Idempotency guard: if the price fields are UNCHANGED from what loaded,
            // keep the exact stored FinalPrice — don't recompute Original−discounts,
            // which would "fix" any pre-existing Final≠Original−discounts anomaly and
            // silently shift commission on an untouched save.
            const _priceUnchanged =
                pOriginal === (Number(sale.originalPrice) || 0) &&
                pAstern === (Number(sale.discountByAstern) || 0) &&
                pAgent === (Number(sale.discountByAgent) || 0);
            pFinal = _priceUnchanged
                ? (Number(sale.finalPrice) || 0)
                : Math.max(0, pOriginal - pAstern - pAgent);
        } else if (s1Edited) {
            // Shared converter (RSC parity): system uses the CC price list; the TOTAL
            // rounds up the whole cash final ONCE; add-on = residual (rows sum to total).
            const _bd = methodBreakdown({ originalPrice, asternDiscount, agentDiscount, addOnPrice }, termForMethod(paymentMethod));
            pOriginal = _bd.original;
            pAstern = _bd.astern;
            pAgent = _bd.agent;
            pAddon = _bd.addon;
            pFinal = _bd.final;
        } else {
            pOriginal = storedPricing?.originalPrice;
            pFinal = storedPricing?.finalPrice;
            pAstern = storedPricing?.asternDiscount;
            pAgent = storedPricing?.agentDiscount;
            pAddon = storedPricing?.addOnPrice;
        }

        const salesData = {
            salesId: sale.salesId,
            updateSalesBySalesIdDto: {
                agentId: sale.agentDetails.userId,
                // Adjustment Edit: send the (possibly changed) co-broke when the
                // Super Admin has the right; otherwise re-send the existing value
                // unchanged so the backend gate never trips for a normal edit.
                coAgentId: canEditCobroke ? (selectedCoBrokeAgentId || null) : (sale.coAgentDetails?.userId ?? null),
                referralUserId: selectedReferralId || null,
                discountByAstern: pAstern || null,
                discountByAgent: pAgent || null,
                panelQty, energy, originalPrice: pOriginal, finalPrice: pFinal,
                addOnPrice: pAddon || null,
                // Send the array (even EMPTY) when the sale has/had line items, so
                // removing the last one clears it. null = "no line-item table" (old
                // lump-sum sales) which the backend leaves untouched. Sending null when
                // the user emptied the table made the removed battery reappear on reload.
                lineItems: ((sale?.lineItems?.length > 0) || lineItems.length > 0) ? lineItems : null,
                productCategory: selectedCategory,
                paymentMethod,
                // Send an explicit status only when an authorised user changed it;
                // otherwise null lets the backend auto-recompute from the criteria.
                status: (canEditStatus && statusTouched.current) ? saleStatus : null,
                depositOn: convertDateStringToDateTime(depositOn) || null,
                // Persist edited PDF/form details + phase so regenerated forms match.
                // Customer name/phone/email/address are mirrored from the Customer
                // section above (single source of truth) into the snapshot.
                formPayloadJson: formPayload ? JSON.stringify({
                    ...formPayload,
                    // Manual sales: re-freeze pricing from the CASH Section-1 inputs (so
                    // the quotation's cash/CC lines tally and section1Cash persists),
                    // but ONLY when Section 1 has a cash value. Untouched old sales (Section
                    // 1 blank) keep their existing frozen snapshot — no re-pricing. RSC/CSC
                    // snapshots (no 'manual' marker) are never re-frozen here.
                    // Re-freeze the quotation snapshot so a regenerated PDF matches the
                    // current price. buildFrozenPricing expects CASH inputs:
                    //  • normal (cash entry): pass the cash Section-1 inputs as-is.
                    //  • edit-in-place (isPrivileged): inputs are the METHOD/stored basis
                    //    (OSP), so back-derive cash = OSP × factor before freezing. This
                    //    keeps the DISPLAY quotation consistent; COMMISSION still comes
                    //    from the Sales row (stored directly, exact) — not this snapshot.
                    ...((formPayload.priceSource === 'manual' && Number(originalPrice) > 0)
                        ? (isPrivileged
                            ? (() => { const f = factorForMethod(paymentMethod); const r = (v) => Math.round((Number(v) || 0) * f * 100) / 100;
                                return buildFrozenPricing({ match: residentialMatch, is3p: phaseMode === '3phase', energy, panelQty,
                                    originalPrice: r(originalPrice), asternDiscount: r(asternDiscount), agentDiscount: r(agentDiscount), addOnPrice: r(addOnPrice),
                                    lineItems, promoBackupbox, promoFirst640, promoPerBatt, paymentMethod }); })()
                            : buildFrozenPricing({ match: residentialMatch, is3p: phaseMode === '3phase', energy, panelQty, originalPrice,
                                asternDiscount, agentDiscount, addOnPrice, lineItems, promoBackupbox, promoFirst640, promoPerBatt, paymentMethod }))
                        : {}),
                    name: (customerName || '').toUpperCase(),
                    phone: customerPhone || '',
                    email: (customerEmail || '').toLowerCase(),
                    address: combinedAddress,
                }) : null,
                phase: phaseMode === '3phase' ? '3phase' : 'single'
            },
            updateCustomerBySalesId: {
                customerName: customerName.toUpperCase(), email: (customerEmail || '').toLowerCase(), phoneNumber: customerPhone, address: (customerAddress || '').toUpperCase(),
                state: customerState || null, city: customerCity || null, postcode: customerPostcode || null
            },
            updatedBy: user.username,
            remarks: remarks || null
        };

        try {
            const res = await updateSalesByAdmin(salesData);
            if (res.status === 200) {
                try {
                    await createLog({
                        module: "Sales", recordId: sale.salesId, action: "Update",
                        // The update response omits `scheme`, which made the change-history
                        // show a phantom "Scheme: ATAP → —" on every save (the DB value is
                        // never touched). Carry the unchanged scheme into newValue so the
                        // diff doesn't report a false change.
                        oldValue: JSON.stringify(sale), newValue: JSON.stringify({ ...res.data, scheme: sale.scheme }),
                        remarks, createdBy: user.username ?? ""
                    });
                } catch { /* log failed silently */ }
                // Field-aware regeneration (user-confirmed 2026-06-18): regenerate only
                // the forms the changed fields affect. Customer/personal info or any
                // Form-Details edit → ALL forms; system size (panels/kWp/phase) →
                // quotation + atap (the contract states the system); price/discount/
                // battery/add-ons/payment method → quotation only. No change → no regen.
                const _n = (v) => Number(v) || 0;
                const _li = (arr) => JSON.stringify((arr || []).map(li => [String(li.itemName || '').trim(), _n(li.qty), _n(li.lineTotal)]));
                const cd = sale.customerDetails || {};
                const customerChanged =
                    (customerName || '').toUpperCase() !== (cd.customerName || '').toUpperCase() ||
                    (combinedAddress || '') !== (cd.address || '') ||
                    (customerEmail || '').toLowerCase() !== (cd.email || '').toLowerCase() ||
                    (customerPhone || '') !== (cd.phoneNumber || '') ||
                    (customerState || '') !== (cd.state || '') ||
                    (customerCity || '') !== (cd.city || '') ||
                    (customerPostcode || '') !== (cd.postcode || '');
                const detailsChanged = formDetailsTouched.current;   // IC/TNB/etc. in the snapshot
                const systemChanged =
                    _n(panelQty) !== _n(sale.panelQty) ||
                    _n(energy) !== _n(sale.energy) ||
                    String(phaseMode === '3phase' ? '3phase' : 'single') !== String(sale.phase || 'single');
                const pricingChanged =
                    _n(pOriginal) !== _n(sale.originalPrice) ||
                    _n(pAstern) !== _n(sale.discountByAstern) ||
                    _n(pAgent) !== _n(sale.discountByAgent) ||
                    _n(pAddon) !== _n(sale.addOnPrice) ||
                    _n(pFinal) !== _n(sale.finalPrice) ||
                    String(paymentMethod || '') !== normalizePaymentMethod(sale.paymentMethod) ||
                    _li(lineItems) !== _li(sale.lineItems);
                let regenTypes = undefined;     // undefined = nothing changed
                if (customerChanged || detailsChanged) regenTypes = null;           // null = ALL forms
                else if (systemChanged) regenTypes = ['quotation', 'atap'];
                else if (pricingChanged) regenTypes = ['quotation'];
                if (regenTypes !== undefined) {
                    try { await generateSaleForms(sale.salesId, false, regenTypes); } catch (e) { console.error("Auto-regenerate forms failed:", e); }
                }
                alert(regenTypes !== undefined
                    ? `Sales updated — ${regenTypes === null ? 'all forms' : regenTypes.join(' + ')} regenerated.`
                    : "Sales updated successfully.");
                formDetailsTouched.current = false;
                setRemarks("");
                setLogRefreshKey(k => k + 1);
                fetchSale();
            }
        } catch (err) {
            console.error("Failed to update sales:", err);
            const data = err.response?.data;
            const serverMsg = (data && (data.message || data.title))
                || (typeof data === 'string' ? data : '');
            alert(serverMsg || "Failed to update sales.");
        } finally {
            setSaving(false);
        }
    };

    // Edit a single field inside the parsed form snapshot (immutable update).
    const handleFormFieldChange = (key, value) => {
        setFormPayload(prev => ({ ...(prev || {}), [key]: value }));
        userModified.current = true;
        formDetailsTouched.current = true;   // snapshot/PDF detail edited → regen all forms
    };

    // Combined one-line address (same format the RSC builder uses) so the snapshot
    // / PDF address always matches the split Customer-section fields above.
    const combinedAddress = [
        customerAddress,
        [customerPostcode, customerCity].filter(s => s && String(s).trim()).join(' '),
        customerState,
    ].filter(s => s && String(s).trim()).join(', ').toUpperCase();

    // Build the ClickUp submission message from the sale + snapshot. Extracts
    // existing data only — adds nothing.
    const buildClickupMsg = () => {
        const agentName = handleAgentName(sale.agentDetails?.firstName, sale.agentDetails?.lastName, sale.agentDetails?.alias);
        return buildClickupExportMsg({ sale, formPayload, agentName, combinedAddress });
    };
    const handleClickupCopy = async () => {
        try {
            await navigator.clipboard.writeText(buildClickupMsg());
            setClickupCopied(true);
            setTimeout(() => setClickupCopied(false), 1800);
        } catch { alert('Could not copy to clipboard.'); }
    };
    const handleClickupWhatsApp = () => {
        window.open(`https://wa.me/?text=${encodeURIComponent(buildClickupMsg())}`, '_blank');
    };

    const openPaymentLinkDialog = () => {
        // Default amount = 100% of the total (Final + Add-on).
        const total = sale?.totalPrice ?? ((finalPrice || 0) + (addOnPrice || 0));
        setPlBank("");
        setPlMethod("One-off payment");
        setPlAmount(total > 0 ? String(total) : "");
        setPlNumLinks(1);
        setPlMsg(null);
        setPlOpen(true);
    };

    const handleRequestPaymentLink = async () => {
        setPlMsg(null);
        const amt = parseFloat(plAmount);
        if (!plBank) { setPlMsg({ type: 'error', text: 'Please select a bank.' }); return; }
        if (!(amt > 0)) { setPlMsg({ type: 'error', text: 'Please enter a valid amount.' }); return; }
        const links = parseInt(plNumLinks, 10);
        if (!(links >= 1)) { setPlMsg({ type: 'error', text: 'Number of links must be at least 1.' }); return; }
        setPlSubmitting(true);
        try {
            await requestPaymentLink({
                salesId: sale.salesId,
                bank: plBank,
                paymentMethod: plMethod,
                amount: amt,
                numLinks: links,
            });
            setPlMsg({ type: 'success', text: 'Payment link requested. It will appear in Remarks & History once filled.' });
            setLogRefreshKey(k => k + 1);
            setTimeout(() => setPlOpen(false), 1200);
        } catch {
            setPlMsg({ type: 'error', text: 'Could not submit the request. Please try again.' });
        } finally {
            setPlSubmitting(false);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><CircularProgress /></Box>;
    if (!sale) return <Box sx={{ p: 4, textAlign: 'center' }}><Typography>Sale not found.</Typography></Box>;

    return (
        <Box sx={{ width: '100%' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
                sx={{ mb: 2, fontFamily: PP, textTransform: 'none', color: '#64748B', fontWeight: 500, fontSize: '0.85rem' }}>
                Back
            </Button>

            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', mb: 0.5 }}>
                Sale #{sale.salesId}
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#64748B', fontFamily: PP, mb: 2 }}>
                {sale.customerDetails?.customerName} &middot; {handleAgentName(sale.agentDetails?.firstName, sale.agentDetails?.lastName, sale.agentDetails?.alias)}
            </Typography>

            {/* Two-column layout: left = form, right = sticky save + collapsible documents + remarks/history */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.5fr) minmax(380px, 1fr)' },
                gap: 2,
                alignItems: 'start',
            }}>
                {/* Left column: sales form on top, doc-only form details at the very end */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                    <GenericSalesForm
                        mode="edit"
                        isSuperAdmin={isSuperAdmin}
                        canEditOriPrice={canEditOriPrice}
                        agentGroup={sale.agentDetails?.groupName}
                        agentName={handleAgentName(sale.agentDetails?.firstName, sale.agentDetails?.lastName, sale.agentDetails?.alias)}
                        coBrokeGroupName={sale.coAgentDetails?.groupName}
                        coBrokeAgentName={sale.coAgentDetails ? handleAgentName(sale.coAgentDetails.firstName, sale.coAgentDetails.lastName, sale.coAgentDetails.alias) : ""}
                        disableCoBrokeFields={!canEditCobroke}
                        handleCoBrokeGroupChange={handleCoBrokeGroupChange}
                        coBrokeGroupOptions={canEditCobroke ? coBrokeGroupOptions : []}
                        coBrokeNameOptions={canEditCobroke ? coBrokeNameOptions : []}
                        selectedCoBrokeGroupId={canEditCobroke ? selectedCoBrokeGroupId : sale.coAgentDetails?.groupId}
                        setSelectedCoBrokeGroupId={() => {}}
                        selectedCoBrokeAgentId={canEditCobroke ? selectedCoBrokeAgentId : sale.coAgentDetails?.userId}
                        setSelectedCoBrokeAgentId={canEditCobroke ? ((v) => { setSelectedCoBrokeAgentId(v); userModified.current = true; }) : () => {}}
                        referralOptions={referralOptions}
                        selectedReferralId={selectedReferralId}
                        setSelectedReferralId={(val) => { setSelectedReferralId(val); userModified.current = true; }}
                        disableReferralField={!canEditReferral}
                        customerName={customerName} setCustomerName={setCustomerName}
                        customerEmail={customerEmail} setCustomerEmail={setCustomerEmail}
                        customerPhone={customerPhone} setCustomerPhone={setCustomerPhone}
                        customerAddress={customerAddress} setCustomerAddress={setCustomerAddress}
                        customerState={customerState} setCustomerState={setCustomerState}
                        customerCity={customerCity} setCustomerCity={setCustomerCity}
                        customerPostcode={customerPostcode} setCustomerPostcode={setCustomerPostcode}
                        selectedCategory={selectedCategory}
                        setSelectedCategory={(val) => { setSelectedCategory(val); userModified.current = true; }}
                        phaseMode={phaseMode}
                        setPhaseMode={(val) => { setPhaseMode(val); userModified.current = true; }}
                        panelQty={panelQty}
                        setPanelQty={(val) => { setPanelQty(val); setEnergy(Number((val * 0.62).toFixed(2))); userModified.current = true; }}
                        energy={energy} setEnergy={setEnergy}
                        originalPrice={originalPrice} setOriginalPrice={setOriginalPrice}
                        salesPriceLens={isPrivileged}
                        ocp={ocp} setOcp={(v) => { setOcp(v); userModified.current = true; }}
                        residentialMatch={residentialMatch}
                        availablePaymentMethods={(paymentMethod && !BASE_PAYMENT_METHODS.some(m => m.value === paymentMethod))
                          ? [...BASE_PAYMENT_METHODS, { label: paymentMethod, value: paymentMethod }]
                          : BASE_PAYMENT_METHODS}
                        paymentMethod={paymentMethod}
                        setPaymentMethod={(val) => { setPaymentMethod(val); userModified.current = true; }}
                        canEditPrice={canEditPrice}
                        depositOn={depositOn} setDepositOn={setDepositOn}
                        asternDiscount={asternDiscount} setAsternDiscount={setAsternDiscount}
                        agentDiscount={agentDiscount} setAgentDiscount={setAgentDiscount}
                        finalPrice={finalPrice} setFinalPrice={setFinalPrice}
                        addOnPrice={addOnPrice} setAddOnPrice={setAddOnPrice}
                        disableAgentFields={true}
                        // All inputs editable by the agent only while Pending; always by
                        // Accountant / Super Admin. (canEditDetails / canEditPricing are
                        // status-aware.)
                        disableCustomerFields={!canEditDetails}
                        disablePricingFields={!canEditPricing}
                        disableProductFields={!canEditDetails}
                        disableDepositDate={!canEditDetails}
                        handleAgentGroupChange={() => {}}
                        lineItems={lineItems}
                        setLineItems={setLineItems}
                        canEditAddons={canEditPricing}
                        showPromos={formPayload?.priceSource === 'manual'}
                        promoBackupbox={promoBackupbox} setPromoBackupbox={setPromoBackupbox}
                        promoFirst640={promoFirst640} setPromoFirst640={setPromoFirst640}
                        promoPerBatt={promoPerBatt} setPromoPerBatt={setPromoPerBatt}
                        // Unlock promos while the sale is Pending (still adjustable); lock
                        // once Active/signed so the frozen discount isn't auto-clobbered.
                        promosLocked={(sale?.status || 'Pending') !== 'Pending'}
                        savedPricing={storedPricing} savedPaymentMethod={paymentMethod}
                        onSizingChange={setSizing}
                    />
                </Paper>

                {/* Document / form details (not sales-related): emergency contact, TIN,
                    IC, TNB account, etc. — kept at the very end of the left column.
                    Feeds the generated PDFs; editing here re-syncs the form snapshot. */}
                <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                    <Box
                        onClick={() => setFormDetailsOpen(o => !o)}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                    >
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', fontSize: '0.95rem' }}>
                                Form Details (for PDFs)
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', fontFamily: PP }}>
                                Customer / document info used only by the generated forms — not part of the sale.
                            </Typography>
                        </Box>
                        <IconButton size="small" sx={{ color: '#64748B' }}>
                            {formDetailsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                    </Box>
                    <Collapse in={formDetailsOpen} unmountOnExit>
                        <Box sx={{ mt: 2 }}>
                            <SaleFormDetails
                                payload={formPayload}
                                onChange={handleFormFieldChange}
                                canEdit={canEditDetails}
                                lockedFields={{
                                    name: (customerName || '').toUpperCase(),
                                    phone: customerPhone || '',
                                    email: (customerEmail || '').toLowerCase(),
                                    address: combinedAddress,
                                }}
                            />
                        </Box>
                    </Collapse>
                </Paper>
                </Box>

                {/* Right column: sticky Save button + collapsible Documents + Remarks/History.
                    Scrolls independently when taller than the viewport so you don't have to
                    scroll the left column down first. */}
                <Box sx={{ position: 'sticky', top: 16, alignSelf: 'start', maxHeight: { md: 'calc(100vh - 32px)' }, overflowY: { md: 'auto' } }}>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                        {/* Sale-status (left) + Save (right), equal width */}
                        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, mb: 1 }}>
                            {(() => {
                                const st = (canEditStatus ? saleStatus : sale.status) || 'Pending';
                                const s = STATUS_STYLES[st] || STATUS_STYLES.Pending;
                                if (canEditStatus) {
                                    return (
                                        <Select
                                            size="small"
                                            value={saleStatus}
                                            onChange={(e) => { setSaleStatus(e.target.value); statusTouched.current = true; }}
                                            sx={{
                                                flex: 1,
                                                fontFamily: PP, fontWeight: 700, fontSize: '0.82rem', color: s.fg,
                                                background: s.bg, borderRadius: '8px',
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: s.bd },
                                                '& .MuiSelect-select': { py: 0.9 },
                                            }}
                                        >
                                            {['Pending', 'Active', 'Refunded', 'Archived'].map(o => (
                                                <MenuItem key={o} value={o} sx={{ fontFamily: PP, fontSize: '0.82rem' }}>{o}</MenuItem>
                                            ))}
                                        </Select>
                                    );
                                }
                                return (
                                    <Box sx={{
                                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        borderRadius: '8px', background: s.bg, border: `1px solid ${s.bd}`,
                                    }}>
                                        <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.82rem', color: s.fg, whiteSpace: 'nowrap' }}>
                                            {st}
                                        </Typography>
                                    </Box>
                                );
                            })()}
                            <Button
                                onClick={handleSave}
                                variant="contained"
                                disabled={saving}
                                sx={{
                                    flex: 1,
                                    background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                                    fontFamily: PP, fontWeight: 600, textTransform: 'none',
                                    borderRadius: '8px', boxShadow: 'none', fontSize: '0.85rem',
                                }}
                            >
                                {saving ? 'Saving...' : isRemarkOnly ? 'Submit Remark' : 'Save Changes'}
                            </Button>
                        </Box>

                        {/* Activation checklist — what's needed before the sale goes Active.
                            Shown to all roles (read-only info), including Group Admin. */}
                        {sale.status !== 'Refunded' && sale.status !== 'Archived' && (() => {
                            const crit = [
                                { ok: !!sale.hasDepositDoc, label: 'Deposit document uploaded' },
                                { ok: !!sale.isQuotationSigned, label: 'Quotation signed' },
                                { ok: !!depositOn, label: 'Deposit date set' },
                            ];
                            const allOk = crit.every(c => c.ok);
                            return (
                                <Box sx={{ mb: 2.5, p: 1.25, borderRadius: '8px', bgcolor: allOk ? '#F0FDF4' : '#FFFBEB', border: `1px solid ${allOk ? '#BBF7D0' : '#FDE68A'}` }}>
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', fontWeight: 600, color: allOk ? '#15803D' : '#B45309', mb: 0.5 }}>
                                        {allOk ? 'All criteria met — sale is Active.' : 'Pending — complete these to activate:'}
                                    </Typography>
                                    {crit.map(c => (
                                        <Box key={c.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                            {c.ok
                                                ? <CheckCircleIcon sx={{ fontSize: 15, color: '#22C55E' }} />
                                                : <RadioButtonUncheckedIcon sx={{ fontSize: 15, color: '#CBD5E1' }} />}
                                            <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: c.ok ? '#475569' : '#92400E' }}>
                                                {c.label}
                                            </Typography>
                                        </Box>
                                    ))}
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.66rem', color: '#94A3B8', mt: 0.5 }}>
                                        Status updates automatically once all three are done. Re-open the sale after uploading the deposit slip or after the customer signs.
                                    </Typography>
                                </Box>
                            );
                        })()}
                    </Paper>

                    {/* Box B — Documents: signature · ClickUp · regenerate · payment link · docs */}
                    <Paper elevation={0} sx={{ p: 2.5, mt: 2, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                        <SalesDocumentsPanel
                            salesId={sale.salesId}
                            canEdit={canUploadDocs}
                            signedOn={sale.signedOn}
                            depositOn={sale.depositOn}
                            refreshKey={logRefreshKey}
                            onDocsChanged={fetchSale}
                            paidTotal={sale.paidTotal || 0}
                            totalPrice={sale.totalPrice || ((finalPrice || 0) + (addOnPrice || 0))}
                            extraActions={(
                                <Box>
                                    <Typography sx={{ fontFamily: PP, fontWeight: 600, fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>
                                        ClickUp Export Msg
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={handleClickupCopy}
                                            sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', borderRadius: '8px', borderColor: '#CBD5E1', color: '#475569' }}
                                        >
                                            {clickupCopied ? 'Copied' : 'Copy'}
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={handleClickupWhatsApp}
                                            sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, borderRadius: '8px', boxShadow: 'none', background: 'linear-gradient(135deg,#25D366,#128C7E)', '&:hover': { background: 'linear-gradient(135deg,#20b558,#0f7360)', boxShadow: 'none' } }}
                                        >
                                            WhatsApp
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                            afterActions={(
                                <Box>
                                    <Typography sx={{ fontFamily: PP, fontWeight: 600, fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>
                                        Payments
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={openPaymentLinkDialog}
                                            sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, borderRadius: '8px', borderColor: '#CBD5E1', color: '#1A8CFF' }}
                                        >
                                            Request Payment Link
                                        </Button>
                                        {refundReq ? (
                                            <Button size="small" variant="outlined" disabled={refundBusy} onClick={cancelRefund}
                                                sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, borderRadius: '8px', borderColor: '#FCA5A5', color: '#DC2626' }}>
                                                Cancel Refund
                                            </Button>
                                        ) : (
                                            <Button size="small" variant="outlined" disabled={refundBusy} onClick={() => { setRefundReason(""); setRefundDlgOpen(true); }}
                                                sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, borderRadius: '8px', borderColor: '#CBD5E1', color: '#DC2626' }}>
                                                Request Refund
                                            </Button>
                                        )}
                                    </Box>
                                    {refundReq && (
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#DC2626', mt: 0.5 }}>
                                            Refund requested{refundReq.requestedByName ? ` by ${refundReq.requestedByName}` : ''}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        />
                    </Paper>

                    {/* Project Timeline — milestone dates (admin / group-admin editable) */}
                    {sale && (
                        <Paper elevation={0} sx={{ p: 2.5, mt: 2, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                            <ProjectTimeline sale={sale} canEdit={canEditTimeline} />
                        </Paper>
                    )}

                    {/* Box C — Remarks & History */}
                    <Paper elevation={0} sx={{ p: 2.5, mt: 2, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                        <Box
                            onClick={() => setHistoryOpen(o => !o)}
                            sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                cursor: 'pointer', py: 1,
                            }}
                        >
                            <Typography sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', fontSize: '0.9rem' }}>
                                Remarks &amp; History
                            </Typography>
                            <IconButton size="small" sx={{ color: '#64748B' }}>
                                {historyOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        </Box>
                        <Collapse in={historyOpen} unmountOnExit>
                            <Box sx={{ pt: 0.5 }}>
                                <LogViewer module="Sales" recordId={sale?.salesId} salesId={sale?.salesId} remarks={remarks} setRemarks={setRemarks} showOnly="sales" refreshKey={logRefreshKey} onSaveRemark={handleSaveRemark} />
                            </Box>
                        </Collapse>
                    </Paper>
                </Box>
            </Box>

            {/* Request Payment Link dialog */}
            <Dialog open={plOpen} onClose={() => !plSubmitting && setPlOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1rem', color: '#0F172A' }}>
                    Request Payment Link
                </DialogTitle>
                <DialogContent>
                    {plMsg && (
                        <Alert severity={plMsg.type} sx={{ mb: 2, fontFamily: PP, fontSize: '0.8rem' }}>{plMsg.text}</Alert>
                    )}
                    <FormControl fullWidth size="small" margin="dense">
                        <InputLabel sx={{ fontFamily: PP }}>Payment method</InputLabel>
                        <Select
                            label="Payment method"
                            value={plMethod}
                            onChange={(e) => { setPlMethod(e.target.value); setPlBank(''); }}
                            sx={{ fontFamily: PP }}
                        >
                            {PL_METHODS.map(m => (
                                <MenuItem key={m} value={m} sx={{ fontFamily: PP }}>{m}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" margin="dense">
                        <InputLabel sx={{ fontFamily: PP }}>Bank</InputLabel>
                        <Select
                            label="Bank"
                            value={plBank}
                            onChange={(e) => setPlBank(e.target.value)}
                            sx={{ fontFamily: PP }}
                        >
                            {banksForMethod(plMethod).map(b => (
                                <MenuItem key={b} value={b} sx={{ fontFamily: PP }}>{b}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Amount (RM)"
                        type="number"
                        value={plAmount}
                        onChange={(e) => setPlAmount(e.target.value)}
                        fullWidth
                        size="small"
                        margin="dense"
                        InputProps={{ sx: { fontFamily: PP } }}
                        InputLabelProps={{ sx: { fontFamily: PP } }}
                    />
                    <TextField
                        label="Number of links"
                        type="number"
                        value={plNumLinks}
                        onChange={(e) => setPlNumLinks(e.target.value)}
                        fullWidth
                        size="small"
                        margin="dense"
                        inputProps={{ min: 1 }}
                        InputProps={{ sx: { fontFamily: PP } }}
                        InputLabelProps={{ sx: { fontFamily: PP } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setPlOpen(false)} disabled={plSubmitting}
                        sx={{ fontFamily: PP, textTransform: 'none', color: '#64748B' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleRequestPaymentLink} variant="contained" disabled={plSubmitting}
                        sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none' }}>
                        {plSubmitting ? 'Submitting...' : 'Submit Request'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Request Refund dialog */}
            <Dialog open={refundDlgOpen} onClose={() => !refundBusy && setRefundDlgOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1rem', color: '#0F172A' }}>
                    Request Refund
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#64748B', mb: 1.5 }}>
                        This flags the sale as refund-requested and logs it in Remarks &amp; History. You can cancel it again anytime.
                    </Typography>
                    <TextField
                        autoFocus fullWidth multiline minRows={2} size="small"
                        label="Reason" value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        sx={{ fontFamily: PP, '& .MuiInputBase-root': { fontFamily: PP, fontSize: '0.85rem' } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRefundDlgOpen(false)} disabled={refundBusy} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={submitRefundRequest} disabled={refundBusy} variant="contained"
                        sx={{ fontFamily: PP, textTransform: 'none', background: '#DC2626', '&:hover': { background: '#B91C1C' } }}>
                        {refundBusy ? 'Submitting…' : 'Request Refund'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
